import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  cellAtPoint,
  completionProof,
  lineCells,
  matchSelection,
  snapEnd,
} from "./wordSearchEngine";

const DIRECTION_HELP = {
  easy: "Words run left to right or top to bottom.",
  medium: "Words run left to right, top to bottom, or diagonally.",
  hard: "Words can run in any of the 8 directions, including backwards.",
};

function cellKey(cell) {
  return cell[0] + ":" + cell[1];
}

function isFocusVisible(el) {
  try {
    return el.matches(":focus-visible");
  } catch (e) {
    return false;
  }
}

/**
 * Word Search player. Drag across letters with a mouse or finger (pointer
 * events), or use the keyboard: arrow keys move, Enter / Space marks the
 * first and last letter, Escape cancels. Invalid selections just clear.
 * onRoundStart fires for every new round; onComplete receives the proof;
 * completionSlot renders inside the success panel.
 */
function WordSearch({
  grid,
  words,
  directions,
  difficulty,
  title,
  onRoundStart,
  onComplete,
  completionSlot,
}) {
  const rows = Array.isArray(grid) ? grid.length : 0;
  const cols = rows ? String(grid[0]).length : 0;
  const wordList = Array.isArray(words) ? words : [];
  const enabled = Array.isArray(directions) ? directions : ["right", "down"];

  const [found, setFound] = useState([]);
  const [selection, setSelection] = useState(null);
  const [cursor, setCursor] = useState([0, 0]);
  const [anchor, setAnchor] = useState(null);
  const [focused, setFocused] = useState(false);
  const [keyboardNav, setKeyboardNav] = useState(false);
  const [status, setStatus] = useState("");
  const [round, setRound] = useState(0);

  const gridRef = useRef(null);
  const dragRef = useRef(null);
  const foundRef = useRef(found);
  const playAgainRef = useRef(null);
  foundRef.current = found;

  useEffect(() => {
    setFound([]);
    setSelection(null);
    setAnchor(null);
    setStatus("");
    if (onRoundStart) onRoundStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  const foundMap = useMemo(() => {
    const map = {};
    found.forEach((f) => {
      map[f.answer] = true;
    });
    return map;
  }, [found]);

  const foundCells = useMemo(() => {
    const cells = {};
    found.forEach((f) => {
      f.cells.forEach((cell) => {
        cells[cellKey(cell)] = true;
      });
    });
    return cells;
  }, [found]);

  const selectedCells = useMemo(() => {
    const cells = {};
    const line = selection ? lineCells(selection.start, selection.end) : null;
    (line || []).forEach((cell) => {
      cells[cellKey(cell)] = true;
    });
    return cells;
  }, [selection]);

  const complete = wordList.length > 0 && found.length === wordList.length;

  useEffect(() => {
    if (complete && playAgainRef.current) playAgainRef.current.focus();
  }, [complete]);

  const commit = (start, end) => {
    const current = foundRef.current;
    if (!start || !end || current.length >= wordList.length) return;
    const answer = matchSelection(grid, start, end, wordList, enabled, foundMapOf(current));
    if (!answer) {
      const cells = lineCells(start, end);
      if (cells && cells.length > 1) setStatus("That isn’t one of the words. Try again.");
      return;
    }
    const entry = { answer, start, end, cells: lineCells(start, end) };
    const next = current.concat([entry]);
    foundRef.current = next;
    setFound(next);
    const label = labelFor(wordList, answer);
    const left = wordList.length - next.length;
    if (left === 0) {
      setStatus(`Found ${label}. All ${wordList.length} words found!`);
      if (onComplete) onComplete(completionProof(next));
    } else {
      setStatus(`Found ${label}. ${left} ${left === 1 ? "word" : "words"} left.`);
    }
  };

  const pointCell = (event) => {
    const el = gridRef.current;
    if (!el) return null;
    return cellAtPoint(el.getBoundingClientRect(), rows, cols, event.clientX, event.clientY);
  };

  const handlePointerDown = (event) => {
    if (complete) return;
    if (event.button !== undefined && event.button !== 0 && event.pointerType === "mouse") return;
    const cell = pointCell(event);
    if (!cell) return;
    event.preventDefault();
    setKeyboardNav(false);
    if (event.currentTarget.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (e) {
        // Best effort; moves still arrive while over the grid.
      }
    }
    dragRef.current = { pointerId: event.pointerId, start: cell };
    setAnchor(null);
    setSelection({ start: cell, end: cell });
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const cell = pointCell(event);
    if (!cell) return;
    setSelection({ start: drag.start, end: snapEnd(drag.start, cell, rows, cols) });
  };

  const handlePointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    const cell = pointCell(event);
    const end = cell ? snapEnd(drag.start, cell, rows, cols) : selection && selection.end;
    setSelection(null);
    commit(drag.start, end);
  };

  const handlePointerCancel = () => {
    dragRef.current = null;
    setSelection(null);
  };

  const handleKeyDown = (event) => {
    if (complete) return;
    if (event.key !== "Tab") setKeyboardNav(true);
    const moves = {
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
    };
    if (moves[event.key]) {
      event.preventDefault();
      const next = [
        Math.max(0, Math.min(rows - 1, cursor[0] + moves[event.key][0])),
        Math.max(0, Math.min(cols - 1, cursor[1] + moves[event.key][1])),
      ];
      setCursor(next);
      if (anchor) setSelection({ start: anchor, end: next });
      return;
    }
    if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      if (!anchor) {
        setAnchor(cursor);
        setSelection({ start: cursor, end: cursor });
        setStatus(`First letter ${grid[cursor[0]].charAt(cursor[1])} marked. Move to the last letter and press Enter.`);
      } else {
        const start = anchor;
        setAnchor(null);
        setSelection(null);
        commit(start, cursor);
      }
      return;
    }
    if (event.key === "Escape") {
      setAnchor(null);
      setSelection(null);
    }
  };

  if (!rows || !cols || !wordList.length) {
    return (
      <div className="games-panel games-panel--error" role="alert">
        <h2 className="games-panel__title">This word search could not be loaded</h2>
        <p className="games-panel__body">Please try another level.</p>
      </div>
    );
  }

  const left = wordList.length - found.length;

  return (
    <div className="wordsearch">
      <div className="wordsearch__controls" role="group" aria-label="Word search controls">
        <span className="wordsearch__progress" aria-live="off">
          {left === 0 ? "All words found" : `${left} of ${wordList.length} words left`}
        </span>
        <button
          type="button"
          className="btn btn-outline-secondary jigsaw__button"
          onClick={() => setRound((r) => r + 1)}
        >
          Restart
        </button>
      </div>
      <p className="jigsaw__note">
        {DIRECTION_HELP[difficulty] || DIRECTION_HELP.easy} Drag from the first letter to the last,
        or focus the grid, move with the arrow keys and press Enter on the first and last letter.
      </p>
      <div className="jigsaw__sr-status" role="status" aria-live="polite">
        {status}
      </div>

      <div className="wordsearch__layout">
        <div
          className="wordsearch__grid"
          ref={gridRef}
          style={{ "--ws-cols": cols, "--ws-rows": rows }}
          tabIndex={complete ? -1 : 0}
          role="application"
          aria-label={`${title || "Word search"} letter grid, ${rows} rows by ${cols} columns`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onKeyDown={handleKeyDown}
          onFocus={(event) => {
            setFocused(true);
            setKeyboardNav(isFocusVisible(event.currentTarget));
          }}
          onBlur={() => {
            setFocused(false);
            setKeyboardNav(false);
          }}
        >
          {grid.map((line, r) =>
            String(line)
              .split("")
              .map((letter, c) => {
                const key = r + ":" + c;
                const isCursor = focused && keyboardNav && cursor[0] === r && cursor[1] === c;
                return (
                  <span
                    key={key}
                    className={
                      "wordsearch__cell" +
                      (foundCells[key] ? " is-found" : "") +
                      (selectedCells[key] ? " is-selected" : "") +
                      (isCursor ? " is-cursor" : "")
                    }
                    aria-hidden="true"
                  >
                    {letter}
                  </span>
                );
              })
          )}
        </div>

        <div className="wordsearch__words">
          <h3 className="wordsearch__words-title">Words to find</h3>
          <ul className="wordsearch__list">
            {wordList.map((word) => (
              <li
                key={word.answer}
                className={"wordsearch__word" + (foundMap[word.answer] ? " is-found" : "")}
              >
                <span className="wordsearch__check" aria-hidden="true">
                  {foundMap[word.answer] ? "✓" : ""}
                </span>
                {word.label}
                {foundMap[word.answer] ? <span className="sr-only"> (found)</span> : null}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {complete ? (
        <div className="games-panel games-panel--success" role="alert">
          <h2 className="games-panel__title">Word search complete!</h2>
          <p className="games-panel__body">You found all {wordList.length} words.</p>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => setRound((r) => r + 1)}
            ref={playAgainRef}
          >
            Play again
          </button>
          {completionSlot || null}
        </div>
      ) : null}
    </div>
  );
}

function foundMapOf(list) {
  const map = {};
  list.forEach((f) => {
    map[f.answer] = true;
  });
  return map;
}

function labelFor(words, answer) {
  for (let i = 0; i < words.length; i++) {
    if (words[i].answer === answer) return words[i].label;
  }
  return answer;
}

export default WordSearch;
